'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DependencyList,
} from 'react';

import { cn } from '@/lib/cn';

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

type BreadcrumbContextValue = {
  override: BreadcrumbItem[] | null;
  setOverride: (id: symbol, items: BreadcrumbItem[] | null) => void;
  clearOverride: (id: symbol) => void;
};

const noop = () => {};

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  override: null,
  setOverride: noop,
  clearOverride: noop,
});

const useBreadcrumbContext = (): BreadcrumbContextValue => {
  return useContext(BreadcrumbContext);
};

type Entry = { id: symbol; items: BreadcrumbItem[] };

export const BreadcrumbProvider = ({ children }: { children: React.ReactNode }) => {
  const [stack, setStack] = useState<Entry[]>([]);

  const setOverride = useCallback((id: symbol, items: BreadcrumbItem[] | null) => {
    setStack((previous) => {
      const without = previous.filter((entry) => entry.id !== id);
      if (items === null) {
        return without;
      }
      return [...without, { id, items }];
    });
  }, []);

  const clearOverride = useCallback((id: symbol) => {
    setStack((previous) => previous.filter((entry) => entry.id !== id));
  }, []);

  const override = stack.length > 0 ? stack[stack.length - 1].items : null;

  const value = useMemo<BreadcrumbContextValue>(
    () => ({ override, setOverride, clearOverride }),
    [override, setOverride, clearOverride],
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export const useBreadcrumb = (factory: () => BreadcrumbItem[] | null, deps: DependencyList): void => {
  const { setOverride, clearOverride } = useBreadcrumbContext();
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) {
    idRef.current = Symbol('breadcrumb');
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(factory, deps);

  useEffect(() => {
    const id = idRef.current!;
    setOverride(id, items);
    return () => {
      clearOverride(id);
    };
  }, [items, setOverride, clearOverride]);
};

type NavKey =
  | 'dashboard'
  | 'daemonTriggers'
  | 'coActions'
  | 'plans'
  | 'documents'
  | 'completionReports'
  | 'codeReviews'
  | 'postMortems'
  | 'conventions'
  | 'skills'
  | 'agentConfigs'
  | 'runnerModels'
  | 'config'
  | 'members'
  | 'feedbacks'
  | 'notifications';

const segmentToNavKey: Record<string, NavKey> = {
  dashboard: 'dashboard',
  'daemon-triggers': 'daemonTriggers',
  'co-actions': 'coActions',
  plans: 'plans',
  documents: 'documents',
  'completion-reports': 'completionReports',
  'code-reviews': 'codeReviews',
  'post-mortems': 'postMortems',
  conventions: 'conventions',
  skills: 'skills',
  'agent-configs': 'agentConfigs',
  feedbacks: 'feedbacks',
  notifications: 'notifications',
};

const systemSegmentToNavKey: Record<string, NavKey> = {
  'runner-models': 'runnerModels',
  config: 'config',
  members: 'members',
};

const useAutoBreadcrumb = (pathname: string): BreadcrumbItem[] => {
  const tNav = useTranslations('nav');

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
      return [];
    }

    const first = segments[0];
    if (first === 'system' && segments[1]) {
      const key = systemSegmentToNavKey[segments[1]];
      if (key) {
        return [{ label: tNav(key) }];
      }
      return [];
    }

    const key = segmentToNavKey[first];
    if (!key) {
      return [];
    }

    return [{ label: tNav(key) }];
  }, [pathname, tNav]);
};

const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="h-3.5 w-3.5 shrink-0 text-[var(--color-fg-subtle)]"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      d="M7.21 14.77a.75.75 0 0 1 .02-1.06L10.94 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.25 4.24c.3.3.3.77 0 1.06l-4.25 4.24a.75.75 0 0 1-1.06.02Z"
      clipRule="evenodd"
    />
  </svg>
);

export const Breadcrumb = () => {
  const { override } = useBreadcrumbContext();
  const pathname = usePathname();
  const autoItems = useAutoBreadcrumb(pathname);
  const items = override ?? autoItems;

  if (items.length === 0) {
    return null;
  }

  const lastIndex = items.length - 1;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0 text-sm">
      <ol className="flex min-w-0 items-center gap-1.5 text-[var(--color-fg-subtle)]">
        {items.map((item, index) => {
          const isLast = index === lastIndex;
          return (
            <li
              key={`${item.label}-${index}`}
              className={cn(
                'flex min-w-0 items-center gap-1.5',
                // Only the last segment is fully visible on mobile; earlier segments collapse
                !isLast && 'hidden sm:flex',
              )}
            >
              {item.href && !isLast ? (
                <Link
                  href={item.href}
                  className="truncate rounded px-1 transition-colors hover:text-[var(--color-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={cn('truncate px-1', isLast && 'text-base font-semibold text-[var(--color-fg)]')}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isLast ? <ChevronIcon /> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
